import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { Button } from '../atoms';

/**
 * ReportsIndex — /v2/reports
 *
 * Lists every registered report from /api/reports. Each card → click
 * navigates to /v2/reports/:slug (ReportViewer).
 *
 * Phase 11 Block 11 PR 3 (2026-05-12).
 */

interface ReportParam {
  name: string;
  required?: boolean;
  default?: unknown;
}
interface ReportMeta {
  slug: string;
  label: string;
  description: string;
  params?: ReportParam[];
}
interface ReportsListResponse {
  reports: ReportMeta[];
}

// A report is runnable from the generic index only if it needs no caller-supplied
// context: every required param must carry a usable default. A report that requires
// a param with no default (e.g. on-sale-tax-bills → parcel_id) is launched from an
// entity page, not browsed here — listing it as a Run/Download card is a dead-end
// (paramless fetch → 400 → the viewer has no way to supply the param).
function runnableFromIndex(r: ReportMeta): boolean {
  return (r.params ?? []).every((p) => !p.required || (p.default !== undefined && p.default !== null));
}

export function ReportsIndex() {
  const navigate = useNavigate();

  const query = useQuery<ReportsListResponse>({
    queryKey: ['reports-list'],
    queryFn: ({ signal }) => api.get<ReportsListResponse>('/api/reports', signal),
  });

  if (query.isLoading) {
    return <p style={{ color: 'var(--color-ink-3)' }}>Loading reports…</p>;
  }

  const reports = (query.data?.reports ?? []).filter(runnableFromIndex);

  return (
    <>
      <header style={{ marginBottom: 'var(--sp-5)' }}>
        <h1 style={{
          margin: 0,
          fontSize: 'var(--fs-3xl)',
          fontWeight: 'var(--fw-semibold)',
          letterSpacing: 'var(--ls-tight)',
          color: 'var(--color-ink)',
        }}>Reports</h1>
        <p style={{
          margin: 'var(--sp-1) 0 0',
          color: 'var(--color-ink-3)',
          fontSize: 'var(--fs-md)',
        }}>
          {reports.length} registered report{reports.length === 1 ? '' : 's'}. Click any card to run it.
        </p>
      </header>

      {reports.length === 0 ? (
        <div style={{
          padding: 'var(--sp-8)',
          background: 'var(--color-card)',
          border: '1px dashed var(--color-border)',
          borderRadius: 'var(--r-lg)',
          color: 'var(--color-muted)',
          textAlign: 'center',
        }}>No reports registered.</div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))',
          gap: 'var(--sp-4)',
        }}>
          {reports.map(r => (
            <ReportCard
              key={r.slug}
              report={r}
              onOpen={() => navigate(`/reports/${r.slug}`)}
              onDownload={() => window.location.assign(`/api/reports/${r.slug}?format=csv`)}
              onDownloadXlsx={() => window.location.assign(`/api/reports/${r.slug}?format=xlsx`)}
              onDownloadPdf={() => window.location.assign(`/api/reports/${r.slug}?format=pdf`)}
            />
          ))}
        </div>
      )}
    </>
  );
}

function ReportCard({
  report, onOpen, onDownload, onDownloadXlsx, onDownloadPdf,
}: {
  report: { slug: string; label: string; description: string };
  onOpen: () => void;
  onDownload: () => void;
  onDownloadXlsx: () => void;
  onDownloadPdf: () => void;
}) {
  return (
    <div style={{
      background: 'var(--color-card)',
      border: '1px solid var(--color-border)',
      borderRadius: 'var(--r-lg)',
      padding: 'var(--sp-4)',
      display: 'flex',
      flexDirection: 'column',
      gap: 'var(--sp-3)',
    }}>
      <div>
        <h3 style={{
          margin: 0,
          fontSize: 'var(--fs-md)',
          fontWeight: 'var(--fw-semibold)',
          color: 'var(--color-ink)',
        }}>{report.label}</h3>
        <p style={{
          margin: 'var(--sp-1) 0 0',
          fontSize: 'var(--fs-sm)',
          color: 'var(--color-ink-3)',
        }}>{report.description}</p>
        <div style={{
          marginTop: 'var(--sp-2)',
          fontFamily: 'var(--font-mono)',
          fontSize: 'var(--fs-xs)',
          color: 'var(--color-muted)',
        }}>{report.slug}</div>
      </div>
      <div style={{ display: 'flex', gap: 'var(--sp-2)', marginTop: 'auto' }}>
        <Button variant="primary" size="sm" onClick={onOpen}>
          Run
        </Button>
        <Button variant="secondary" size="sm" onClick={onDownload}>
          Download CSV
        </Button>
        <Button variant="secondary" size="sm" onClick={onDownloadXlsx}>
          Export XLSX
        </Button>
        <Button variant="secondary" size="sm" onClick={onDownloadPdf}>
          Export PDF
        </Button>
      </div>
    </div>
  );
}
